/**
 * Deal Card — kanban card component showing deal summary.
 * Draggable via @dnd-kit.
 */

import { useDraggable } from '@dnd-kit/core'
import { User, Clock } from 'lucide-react'
import { useCrmStore } from '@/stores/crmStore'
import { formatCents } from '@/utils/currency'
import type { Deal } from '@/types/crm'

interface DealCardProps {
  deal: Deal
  isDragging?: boolean
}

export function DealCard({ deal, isDragging }: DealCardProps) {
  const setSelectedDeal = useCrmStore((s) => s.setSelectedDeal)

  const { attributes, listeners, setNodeRef, transform } = useDraggable({
    id: deal.id,
  })

  const style = transform
    ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)` }
    : undefined

  const timeInStage = () => {
    if (!deal.stageEnteredAt) return null
    const entered = new Date(deal.stageEnteredAt)
    const now = new Date()
    const diffMs = now.getTime() - entered.getTime()
    const days = Math.floor(diffMs / (1000 * 60 * 60 * 24))
    if (days === 0) return 'today'
    if (days === 1) return '1d'
    return `${days}d`
  }

  const stageTime = timeInStage()

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...listeners}
      {...attributes}
      onClick={(e) => {
        e.stopPropagation()
        setSelectedDeal(deal)
      }}
      className={`bg-card rounded-lg border border-border p-3 cursor-pointer hover:border-primary/40 hover:shadow-xs transition-all ${
        isDragging ? 'opacity-50 shadow-lg ring-2 ring-primary' : ''
      }`}
    >
      <p className="text-sm font-medium text-foreground mb-1.5 line-clamp-2">
        {deal.name}
      </p>

      {deal.contactName && (
        <div className="flex items-center gap-1 text-xs text-muted-foreground mb-1.5">
          <User className="w-3 h-3" />
          <span className="truncate">{deal.contactName}</span>
        </div>
      )}

      <div className="flex items-center justify-between">
        {deal.amountCents != null ? (
          <span className="text-sm font-semibold text-foreground">{formatCents(deal.amountCents, deal.currency)}</span>
        ) : (
          <span className="text-xs text-muted-foreground italic">No amount</span>
        )}

        {stageTime && (
          <span className="flex items-center gap-0.5 text-xs text-muted-foreground">
            <Clock className="w-3 h-3" />
            {stageTime}
          </span>
        )}
      </div>

      {/* Tags */}
      {deal.tags && deal.tags.length > 0 && (
        <div className="flex gap-1 mt-2 flex-wrap">
          {deal.tags.slice(0, 2).map((tag) => (
            <span key={tag} className="px-1.5 py-0.5 text-[10px] rounded-sm bg-muted text-muted-foreground">
              {tag}
            </span>
          ))}
        </div>
      )}
    </div>
  )
}
